import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { LANDING_TEMPLATES, LandingTemplateId } from '../../data/landingTemplates';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [template, setTemplate] = useState<LandingTemplateId>('caso_exito');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    const landing = await createLanding(organizationId, name.trim(), formId || undefined, template);
    setSaving(false);
    setCreating(false);
    setName('');
    setFormId('');
    setTemplate('caso_exito');
    navigate(`/app/landings/${landing.id}/editor`);
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1205" data-mp="ggv9csw0bja0" />} onClick={() => setCreating(true)} data-id="element-1204" data-mp="ggd3d4jjlqow">
            Nueva landing
          </Button>} data-id="element-1203" data-mp="2h40t7o9ev3e" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-1207" data-mp="3jxbsht1uc5a8" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-1208" data-mp="3ftxdn5pn1uyd">Crear landing</Button>} data-id="element-1206" data-mp="bffnwzpx3ar3" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1209" data-mp="3topx2f8nfva8">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1210" data-mp="1bpyut1h0d2i9">
                <div className="flex items-start justify-between gap-3" data-id="element-1211" data-mp="1ghgwxsurp94t">
                  <div className="min-w-0" data-id="element-1212" data-mp="12pahya3cp86y">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1213" data-mp="12qlkk6l289wh">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-1214" data-mp="1vblg7e3hix0l">
                      {landingUrl(landing.slug)}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1216" data-mp="1lhw7ydrku4jm">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1217" data-mp="a2ppf6b52q1t" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1218" data-mp="ub7emnsumdln" />,
              onSelect: () => window.open(`/landing/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-1219" data-mp="lh175ypl3i6v" />,
              onSelect: async () => {
                const ok = await copyToClipboard(landingUrl(landing.slug));
                if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace');
              }
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1220" data-mp="146r873jxx078" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Actualizar publicación' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1221" data-mp="3oyj8dnaupqyb" />,
              onSelect: async () => {
                await setLandingStatus(landing.id, 'publicada');
                toast.success('Landing publicada correctamente', {
                  description: landingUrl(landing.slug)
                });
              }
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1222" data-mp="3js4ebmm6mn9o" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-1215" data-mp="1leqll6ac1grl" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1223" data-mp="3skzngrert34q">
                  <div data-id="element-1224" data-mp="3skpviya60lwe">
                    <dt className="text-xs text-muted-foreground" data-id="element-1225" data-mp="3snc4n2cpf9gq">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1226" data-mp="1ctvn0bt5g1e">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-1227" data-mp="6bx0okrsfm9d">
                    <dt className="text-xs text-muted-foreground" data-id="element-1228" data-mp="bcks37jxi544">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1229" data-mp="bai0wk71i5xt">{landing.views}</dd>
                  </div>
                  <div data-id="element-1230" data-mp="2d47ltgv7ge4a">
                    <dt className="text-xs text-muted-foreground" data-id="element-1231" data-mp="2d3foa0i1s6bd">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-1232" data-mp="2539mf8rak4u1">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-1233" data-mp="206ja8wilxtdw">
                    <dt className="text-xs text-muted-foreground" data-id="element-1234" data-mp="2f1fobqd3layw">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-1235" data-mp="2ezcoyfq225hi">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-1236" data-mp="2o1lyb29l7stb">
                  <Tag data-id="element-1237" data-mp="2j3u7kbkoicqp">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-1238" data-mp="2j180se5m419j">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-1240" data-mp="2svgfnz1pzvp8">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1241" data-mp="36twte9y02mgv">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim()} data-id="element-1242" data-mp="32suww0vgwoil">
              Crear
            </Button>
          </div>} data-id="element-1239" data-mp="2st3xbks1vjbn">
        <div className="space-y-5" data-id="element-1243" data-mp="3qeoninyybsr4">
          <fieldset data-id="element-1244" data-mp="3qdm1oughkgi2">
            <legend className="mb-2 text-sm font-medium text-foreground" data-id="element-1245" data-mp="3qe5a9kk8xsjf">
              Plantilla de partida
            </legend>
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-1246" data-mp="3ch8ldonoul56">
              {LANDING_TEMPLATES.map(option => {
              const active = template === option.id;
              return <button key={option.id} type="button" onClick={() => setTemplate(option.id)} aria-pressed={active} className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-id="element-1247" data-mp="32njdipfozuoq">
                    <span className="block text-sm font-semibold text-foreground" data-id="element-1248" data-mp="2xp82wchkpg5g">
                      {option.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground" data-id="element-1249" data-mp="2jsbd8ed6y4q5">
                      {option.description}
                    </span>
                  </button>;
            })}
            </div>
          </fieldset>
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-1250" data-mp="2z7q7bjah6aro" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1251" data-mp="2v8r546g4os3x" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-1252" data-mp="2ggyqxxs2lk3s" />
    </>;
}